<template>
    <div class="sales-rep-table">
        <v-data-table-server
            :items="items"
            :headers="headers"
            :loading="loading"
            :loading-text="$t('common.loading')"
            :no-data-text="$t('common.noDataText')"
            :items-per-page-text="`${$t('common.rowsPerPage')}`"
            :page-text="`${currentPage} ${$t('common.of')} ${lastPage}`"
            :items-per-page-options="itemsPerPageOptions"
            :items-length="total"
            :options="options"
            fixed-header
            :height="items.length > 10 ? '60vh' : '100%'"
            @update:options="optionsUpdate"
            @click:row="onRowClick"
        >
            <template #loading>
                <div class="sales-rep-table__loading">{{ $t('common.loading') }}</div>
            </template>

            <template #top>
                <div class="kasanowa-top-filter-wrapper">
                    <KTextField
                        id="search"
                        v-model="search"
                        name="search"
                        label=""
                        :placeholder="$t('common.search')"
                        hide-details
                        clearable
                    />
                </div>
            </template>

            <template #item.ph_id="elem">
                <div v-if="elem.item.id != null">{{ elem.item.id }}</div>
                <div v-else class="sales-rep-table__none">{{ $t('salesRep.none') }}</div>
            </template>

            <template #item.id="elem">
                <div v-if="elem.item.id != null">{{ elem.item.id }}</div>
                <div v-else class="sales-rep-table__none">{{ $t('salesRep.none') }}</div>
            </template>

            <template #item.asseco_id="elem">
                <div v-if="elem.item.asseco_id != null">{{ elem.item.asseco_id }}</div>
                <div v-else class="sales-rep-table__none">{{ $t('salesRep.none') }}</div>
            </template>

            <template #item.name_and_surname="elem">
                <div v-if="elem.item.name != null && elem.item.surname != null">{{ elem.item.name }} {{ elem.item.surname }}</div>
                <div v-else class="sales-rep-table__none">{{ $t('salesRep.none') }}</div>
            </template>

            <template #item.email="elem">
                <div v-if="elem.item.email != null">{{ elem.item.email }}</div>
                <div v-else class="sales-rep-table__none">{{ $t('salesRep.none') }}</div>
            </template>

            <template #item.wholesales="elem">
                <div v-if="elem.item.warehouses != null && elem.item.warehouses.length > 0" class="sales-rep-table__column">
                    <div v-for="(warehouse, index) in elem.item.warehouses"><span>{{ index + 1 }}.</span> {{ warehouse.name }}</div>
                </div>
                <div v-else class="sales-rep-table__none">{{ $t('salesRep.none') }}</div>
            </template>

            <template #item.card_number="elem">
                <div v-if="elem.item.card != null && elem.item.card.number != null">{{ elem.item.card.number }}</div>
                <div v-else class="sales-rep-table__none">{{ $t('salesRep.none') }}</div>
            </template>

            <template #item.active="elem">
                <div v-if="elem.item.last_login_at != null">{{ $t('salesRep.yes') }}</div>
                <div v-else class="sales-rep-table__none">{{ $t('salesRep.no') }}</div>
            </template>

            <template #item.complete_profile="elem">
                <div v-if="checkIsProfileCompleted(elem.item)">{{ $t('salesRep.yes') }}</div>
                <div v-else class="sales-rep-table__none">{{ $t('salesRep.no') }}</div>
            </template>

            <template #item.last_login="elem">
                <div v-if="elem.item.last_login_at != null">{{ useDataFormatWithTime(elem.item.last_login_at) }}</div>
                <div v-else class="sales-rep-table__none">{{ $t('salesRep.never') }}</div>
            </template>

            <template #item.login_last_14_days="elem">
                <div v-if="elem.item.login_counter_weekly != null">{{ elem.item.login_counter_weekly }}</div>
                <div v-else class="sales-rep-table__none">{{ $t('salesRep.never') }}</div>
            </template>

            <template #item.challenges="elem">
                <div v-if="elem.item.challenges != null && elem.item.challenges.length > 0">
                    <div v-for="item in elem.item.challenges" :key="item.name" class="sales-rep-table__challenges-wrapper">
                        <div>{{ item.name }}</div>
                        <div>{{ item.amount }} PLN</div>
                    </div>
                </div>
                <div v-else>0.00 PLN</div>
            </template>

            <template #item.total_amount="elem">
                <div v-if="elem.item.total_amount != null">{{ elem.item.total_amount }} PLN</div>
                <div v-else>0.00 PLN</div>
            </template>

            <template #item.amount_from_quiz="elem">
                <div v-if="elem.item.amount_from_quiz != null">{{ elem.item.amount_from_quiz }} PLN</div>
                <div v-else>0.00 PLN</div>
            </template>

            <template #item.amount_to_withdraw="elem">
                <div v-if="elem.item.wallet != null && elem.item.wallet.in_withdrawal != null">{{ elem.item.wallet.in_withdrawal.split('.')[0] }} PLN</div>
                <div v-else>0.00 PLN</div>
            </template>

            <template #item.withdrawn_amount="elem">
                <div v-if="elem.item.withdraw_amount != null">{{ elem.item.withdraw_amount }} PLN</div>
                <div v-else>0.00 PLN</div>
            </template>

            <template #item.send_link="elem">
                <div class="sales-rep-table__center">
                    <KButton
                        id="send-link-btn"
                        :label="$t('salesRep.sendActivateLinkBtn')"
                        type="submit"
                        :loading="activeLoading"
                        @click.stop="sendActiveLink(elem.item)"
                    />
                </div>
            </template>

            <template #item.actions="elem">
                <div class="sales-rep-table__action-wrapper" @click.stop>
                    <KTooltip without-btn>
                        <template v-slot:title>
                            <div class="kasanowa-hover sales-rep-table__icon-wrapper sales-rep-table__icon-wrapper--edit" @click="navigateTo(`/sales-rep/${elem.item.id}`)">
                                <KIcon icon="edit" :color="grey400" />
                            </div>
                        </template>
                        <template v-slot:content>
                            <div>{{ $t('warehouse.edit') }}</div>
                        </template>
                    </KTooltip>
                </div>
            </template>

            <template #footer.prepend>
                <div class="kasanowa-total-table-counter">{{ $t('common.total') }}: {{ total }}</div>
            </template>
        </v-data-table-server>
    </div>
</template>

<script setup lang="ts">
import { defineProps, ref, watch } from 'vue';
import router from '@/router';
import { itemsPerPageOptions } from '@/hooks/TableSettingsHooks';
import SalesRepApi from '@/api/v1/SalesRepApi';
import { toast } from 'vue3-toastify';
import { useDataFormatWithTime } from '@/hooks/DataHooks';
import { navigateTo } from '@/hooks/NavigateHooks';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import variables from '@/assets/scss/modules/variables.module.scss';
import type { ITableHeader, ITableOptions } from '@/models/TableSettingsModel';
import type { ISalesRepItemResponse } from '@/models/SalesRepModel';
import KTextField from '@/components/atoms/KTextField.vue';
import KTooltip from '@/components/atoms/KTooltip.vue';
import KIcon from '@/components/atoms/KIcon.vue';
import KButton from '@/components/atoms/KButton.vue';

interface IOwnProps {
    isWarehouseView?: boolean
    isSalesRepReg?: boolean
}
interface IProps extends IOwnProps {
}

const props = defineProps<IProps>();
const { grey400 } = variables;
const { isAdmin, isAdminColian } = storeToRefs(useUserStore());

const headers = ref<ITableHeader[]>([]);
const defaultHeaders = (): ITableHeader[] => {
    const result: ITableHeader[] = [];

    if (!props.isSalesRepReg) {
        result.push(
            {
                title: t('salesRep.idPh'),
                align: 'start',
                sortable: false,
                key: 'ph_id',
                width: '100px',
                minWidth: '100px',
            },
            {
                title: t('salesRep.idAsseco'),
                align: 'center',
                sortable: false,
                key: 'asseco_id',
                width: '100px',
                minWidth: '100px',
            },
            {
                title: t('salesRep.nameAndSurname'),
                align: 'center',
                sortable: false,
                key: 'name_and_surname',
                width: '',
                minWidth: '',
            },
            {
                title: t('salesRep.cardNumber'),
                align: 'center',
                sortable: false,
                key: 'card_number',
                width: '',
                minWidth: '',
            },
        );
    }

    if (props.isSalesRepReg) {
        result.push(
            {
                title: t('salesRep.id'),
                align: 'start',
                sortable: false,
                key: 'id',
                width: '100px',
                minWidth: '100px',
            },
            {
                title: t('salesRep.email'),
                align: 'start',
                sortable: false,
                key: 'email',
                width: '',
                minWidth: '',
            },
            {
                title: t('salesRep.wholesales'),
                align: 'start',
                sortable: false,
                key: 'wholesales',
                width: '',
                minWidth: '',
            },
        );
    }

    if (!props.isSalesRepReg) {
        result.push(
            {
                title: t('salesRep.challenges'),
                align: 'center',
                sortable: false,
                key: 'challenges',
                width: '160px',
                minWidth: '160px',
            },
            {
                title: t('salesRep.amountTotal'),
                align: 'center',
                sortable: false,
                key: 'total_amount',
                width: '120px',
                minWidth: '120px',
            },
            {
                title: t('salesRep.moneyForQuiz'),
                align: 'center',
                sortable: false,
                key: 'amount_from_quiz',
                width: '120px',
                minWidth: '120px',
            },
            {
                title: t('salesRep.amountToWithdraw'),
                align: 'center',
                sortable: false,
                key: 'amount_to_withdraw',
                width: '120px',
                minWidth: '120px',
            },
            {
                title: t('salesRep.withdrawnAmount'),
                align: 'center',
                sortable: false,
                key: 'withdrawn_amount',
                width: '120px',
                minWidth: '120px',
            },
        );
    }

    result.push(
        {
            title: t('salesRep.sendActivateLink'),
            align: 'center',
            sortable: false,
            key: 'send_link',
            width: '100px',
            minWidth: '100px',
        },
    );

    if (isAdminColian.value || isAdmin.value) {
        result.push({
            title: '',
            align: 'center',
            sortable: false,
            key: 'actions',
            width: '50px',
            minWidth: '50px',
        });
    }

    return result;
};
headers.value = defaultHeaders();

const loading = ref<boolean>(false);
const currentPage = ref<number>(0);
const lastPage = ref<number>(0);
const total = ref<number>(0);
const items = ref<ISalesRepItemResponse[]>([]);
const salesRepApi = new SalesRepApi();
const salesRepList = async() => {
    loading.value = true;
    if (!props.isWarehouseView) {
        try {
            const result = await salesRepApi.salesRepList(
                options.value.page,
                options.value.itemsPerPage,
                options.value.sortBy.length > 0 ? options.value.sortBy[0].key : null,
                options.value.sortBy.length > 0 ? options.value.sortBy[0].order : null,
                props.isSalesRepReg,
                search.value,
            );
            if (!result.isSuccess) {
                toast.error(t('salesRep.toast.errorSalesRepList'));
                loading.value = false;
                return;
            }
            currentPage.value = result.payload.current_page;
            lastPage.value = result.payload.last_page;
            total.value = result.payload.total;
            items.value = result.payload.data;
        } catch (e) {
            toast.error(t('salesRep.toast.errorSalesRepList'));
            loading.value = false;
            return;
        }
    } else {
        try {
            const result = await salesRepApi.warehouseSalesRepList(
                options.value.page,
                options.value.itemsPerPage,
                options.value.sortBy.length > 0 ? options.value.sortBy[0].key : null,
                options.value.sortBy.length > 0 ? options.value.sortBy[0].order : null,
                router.currentRoute.value.params.id,
                search.value,
            );
            if (!result.isSuccess) {
                toast.error(t('salesRep.toast.errorSalesRepList'));
                loading.value = false;
                return;
            }
            currentPage.value = result.payload.current_page;
            lastPage.value = result.payload.last_page;
            total.value = result.payload.total;
            items.value = result.payload.data;
        } catch (e) {
            toast.error(t('salesRep.toast.errorSalesRepList'));
            loading.value = false;
            return;
        }
    }
    loading.value = false;
};
const options = ref<ITableOptions>({
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    groupBy: [],
    search: null,
});
const optionsUpdate = (val:ITableOptions) => {
    options.value.page = val.page;
    options.value.itemsPerPage = val.itemsPerPage;
    options.value.search = val.search;
    options.value.groupBy = val.groupBy;
    options.value.sortBy = val.sortBy;
    salesRepList();
};

const onRowClick = (event: any, item: any) => {
    navigateTo(`/sales-rep/details/${ item.item.id }`);
};

const search = ref<string | null>(null);
const searchInterval = ref<any>(undefined);
watch(() => search.value, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        clearTimeout(searchInterval.value);
    }
    searchInterval.value = setTimeout(() => {
        salesRepList();
    }, 700);
}, {
    deep: true
});

const checkIsProfileCompleted = (item: ISalesRepItemResponse): boolean => {
    return (
        item.name != null &&
        item.name !== '' &&
        item.surname != null &&
        item.surname !== '' &&
        item.email != null &&
        item.email !== '' &&
        item.asseco_id != null &&
        item.asseco_id !== ''
    )
};

const activeLoading = ref<boolean>(false);
const sendActiveLink = async(item: ISalesRepItemResponse) => {
    activeLoading.value = true;
    try {
        const result = await salesRepApi.sendActivateLinkToUser({
            email: item.email,
        });
        if (!result.isSuccess) {
            toast.error(t('salesRep.toast.errorSendActiveLink'));
            activeLoading.value = false;
            return;
        }
        toast.success(t('salesRep.toast.successSendActiveLink'));
    } catch (e) {
        toast.error(t('salesRep.toast.errorSendActiveLink'));
        activeLoading.value = false;
        return;
    }
    activeLoading.value = false;
};
</script>

<style scoped lang="scss">
.sales-rep-table {
    &__loading {
        opacity: 0.6;
    }

    &__none {
        color: $red-400;
    }

    &__action-wrapper {
        display: flex;
        justify-content: space-around;
    }

    &__icon-wrapper {
        padding: 5px;
        height: 26px;
    }

    &__btn-dialog-wrapper {
        margin-top: 10px;
        display: flex;
        flex-direction: column;
        gap: 10px;

        @include media-breakpoint-up(sm) {
            margin-top: 20px;
            flex-direction: row;
            width: 100%;
            justify-content: space-around;
        }
    }

    &__dialog-title {
        font-size: 18px;
        letter-spacing: 0;
    }

    &__challenges-wrapper {
        display: flex;
        gap: 5px;
        justify-content: space-around;
    }

    &__action-wrapper {
        display: flex;
        justify-content: space-around;
    }

    &__icon-wrapper {
        padding: 5px;
        height: 26px;
    }

    &__center {
        width: max-content;
        margin: 0 auto;
    }

    &__column {
        display: flex;
        flex-direction: column;
        gap: 3px;

        span {
            font-weight: 600;
        }
    }

    &:deep(.v-data-table tr:hover) {
        .sales-rep-table__icon-wrapper--edit svg {
            fill: $primary-800;
        }

        .sales-rep-table__icon-wrapper--trash svg {
            fill: $red-400;
        }
    }
}
</style>
